import { Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { GrupoDTO, GrupoResponseDTO } from '../../../models/grupo.model';
import { GrupoService } from '../../../services/grupo/grupo.service';
import { SafeStorageService } from '../../safe-storage.service';

@Component({
  selector: 'app-select-grupo',
  templateUrl: './select-grupo.component.html',
  styleUrls: ['./select-grupo.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectGrupoComponent),
      multi: true
    }
  ]
})
export class SelectGrupoComponent
  implements ControlValueAccessor, OnInit, OnDestroy {

  /* ======================= *
   * Inputs
   * ======================= */
  @Input() placeholder: string = '';
  @Input() returnType: 'object' | 'id' = 'object';
  @Input() showAddButton: boolean = true;

  /* ======================= *
   * Estado interno
   * ======================= */
  env = environment;
  idEmpresa: string = '';
  grupos: GrupoDTO[] = [];
  loading = false;
  search = '';
  page = 0;
  size = 20;
  totalRecords = 0;
  hasMore = true; // Flag para indicar se há mais registros
  isLoadingMore = false; // Flag para carregamento incremental

  /** valor que o p-dropdown usa */
  value: any = null;

  /** Visibilidade do dialog de nova categoria */
  dialogVisible: boolean = false;

  /** Texto do botao "+ Agregar Categoria" (carregado via i18n) */
  textoAgregarCategoria: string = '';

  private langSub?: Subscription;

  /* ======================= *
   * ControlValueAccessor
   * ======================= */
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private safe: SafeStorageService,
    private grupoService: GrupoService,
    private translate: TranslateService
  ) { }

  /* ======================= *
   * Lifecycle
   * ======================= */
  ngOnInit(): void {
    this.idEmpresa = this.safe.getItem('id') || '';
    this.loadInitial(); // Carrega dados iniciais
    this.loadTextoAgregar();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTextoAgregar();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  /* ======================= *
   * ControlValueAccessor impl
   * ======================= */
  writeValue(value: any): void {
    this.value = value;

    // Se já recebeu valor (edição) e a lista já existe, sincroniza
    if (value && this.grupos.length) {
      this.syncSelectedValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /* ======================= *
   * Seleção
   * ======================= */
  onSelect(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  /* ======================= *
   * Carga inicial
   * ======================= */
  loadInitial(): void {
    this.loading = true;
    this.page = 0;
    this.hasMore = true;
    this.grupos = []; // Limpa lista existente

    const params: any = {
      page: this.page,
      size: this.size,
      sort: 'id,asc',
    };
    if (this.search) {
      params['filter'] = `&nome=${this.search}`;
    }

    this.grupoService.get('/grupo/filtrarGrupo', params).subscribe({
      next: (res: GrupoResponseDTO) => {
        this.grupos = [...res.content];
        this.totalRecords = res.totalElements || 0;
        this.hasMore = this.grupos.length < this.totalRecords;
        this.syncSelectedValue();
      },
      error: () => {
        // Grupos são opcionais — falha silenciosa
        this.grupos = [];
        this.hasMore = false;
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }


  /* ======================= *
   * Filtro remoto
   * ======================= */
  onFilter(event: any): void {
    this.search = event.filter?.trim() || '';
    this.loadInitial(); // Reinicia busca com novo filtro
  }

  /* ======================= *
   * Sincronização edição
   * ======================= */
  private syncSelectedValue(): void {
    if (!this.value) return;

    // quando retorna id, garantir que o grupo exista na lista
    if (this.returnType === 'id') {
      const exists = this.grupos.some(g => g.id === this.value);
      if (!exists) {
        this.loadGrupoById(this.value);
      } else {
        // Força o PrimeNG a re-avaliar o selectedOption quando a lista
        // chega depois que o value já estava setado (race condition de edição)
        const valorAtual = this.value;
        this.value = null;
        setTimeout(() => { this.value = valorAtual; });
      }
    }
  }

  private loadGrupoById(id: number): void {
    this.grupoService.get(`/grupo/${id}`).subscribe({
      next: (grupo: GrupoResponseDTO) => {
        this.grupos = [grupo as any, ...this.grupos];
      },
      error: (err) => {
        if (err?.status === 404) {
          console.warn(`Grupo com ID ${id} não encontrado.`);
          return;
        }
        console.error('Erro ao carregar grupo por ID:', err);
      }
    });
  }

  /* ======================= *
   * Dialog de nova categoria
   * ======================= */

  /**
   * Handler do click no footer "+ Agregar Categoria".
   * Usa (mousedown) com stopPropagation para impedir
   * que o dropdown feche antes de processar o clique.
   */
  onAddNew(event: MouseEvent): void {
    event.stopPropagation();
    this.dialogVisible = true;
  }

  /**
   * Handler chamado quando o dialog emite uma categoria criada.
   * Insere a categoria no inicio da lista e a seleciona automaticamente.
   */
  onGrupoCriado(grupo: GrupoDTO): void {
    // Inserir a categoria no inicio da lista
    this.grupos = [grupo, ...this.grupos];
    this.totalRecords += 1;

    // Selecionar a categoria recem-criada
    if (this.returnType === 'id') {
      this.value = grupo.id;
    } else {
      this.value = grupo;
    }

    // Propagar a mudanca para o formulario pai
    this.onChange(this.value);
  }

  /* ======================= *
   * Util
   * ======================= */
  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src =
      'assets/images/profile_placeholder.png';
  }

  private loadTextoAgregar(): void {
    this.textoAgregarCategoria = this.translate.instant('select-categoria.botoes.agregar_categoria');
  }
}